/** @jsx jsx */

import { Fragment } from 'react';

import { BrandButton, Colors, Link, Text } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import { MEDIA_MIN_DESKTOP, MEDIA_MIN_TABLET } from './constants';

const Styles = {
  item: css`
    display: flex;
    list-style: none;
    :hover {
      background-color: ${Colors.blue10};
      color: ${Colors.blue};
    }
    ${[MEDIA_MIN_DESKTOP]} {
      display: inline-flex;
      :hover {
        background-color: initial;
        color: inherit;
      }
    }
  `,
  link: ({
    isActive,
    isLightTheme,
  }: {
    isActive: boolean;
    isLightTheme: boolean;
  }) => css`
    color: inherit;
    display: block;
    padding: 15px 40px;
    text-decoration: none;
    width: 100%;
    ${[MEDIA_MIN_DESKTOP]} {
      border-bottom: 4px solid
        ${isActive
          ? isLightTheme
            ? Colors.zapier
            : Colors.neutral100
          : 'transparent'};
      margin: 0px 17px;
      padding: 21px 3px 17px;
      width: initial;
      :hover {
        border-bottom-color: ${isLightTheme
          ? Colors.zapier
          : Colors.neutral100};
        color: inherit;
      }
    }
  `,
  list: css`
    margin: 0px;
    padding: 0px;
  `,
  root: css`
    border-top: 1px solid ${Colors.neutral400};
    box-shadow: rgba(0, 0, 0, 0.2) 0px 30px 30px 0px;
    margin: 0px;
    ${[MEDIA_MIN_DESKTOP]} {
      background-color: initial;
      border: initial;
      box-shadow: initial;
      display: inline-block;
      flex: 1;
    }
  `,
  signUpWrapper: css`
    display: flex;
    padding: 15px 0px 15px 40px;
    ${[MEDIA_MIN_TABLET]} {
      display: none;
    }
  `,
};

type NavLink = {
  href: string;
  label: string;
  visibility: 'users' | 'visitors' | 'everyone';
};

type Props = {
  currentUrl: string;
  isAuthed: boolean;
  isLightTheme: boolean;
  links: NavLink[];
  onClickLink?: ({ href, label }: NavLink) => void;
  urlSignUp: string;
};

const defaultProps = {
  links: undefined,
  onClickLink: () => undefined,
};

function NavLinks({
  links,
  currentUrl,
  isAuthed,
  isLightTheme,
  onClickLink = defaultProps.onClickLink,
  urlSignUp,
}: Props) {
  const isVisible = (visibility: NavLink['visibility']) => {
    return {
      everyone: true,
      users: isAuthed,
      visitors: !isAuthed,
    }[visibility];
  };

  return (
    <Fragment>
      {!isAuthed && (
        <div css={Styles.signUpWrapper}>
          <BrandButton href={urlSignUp}>Sign up</BrandButton>
        </div>
      )}
      <nav css={Styles.root}>
        <ul css={Styles.list}>
          {links.map(({ href, label, visibility }) =>
            isVisible(visibility) ? (
              <li css={Styles.item} key={label}>
                <Link
                  css={Styles.link({
                    isActive: href === currentUrl,
                    isLightTheme,
                  })}
                  href={href}
                  onClick={() => onClickLink({ href, label, visibility })}
                >
                  <Text color="inherit" type="paragraph3">
                    {label}
                  </Text>
                </Link>
              </li>
            ) : null
          )}
        </ul>
      </nav>
    </Fragment>
  );
}

NavLinks.defaultProps = defaultProps;

export default NavLinks;
